/**
 * Buddy Slot Component
 * Visual representation of an automated fishing buddy
 */

import { useEffect, useState } from 'react';
import { FishingBuddy, calculateStatMultipliers, GameEvent } from '@/types';
import { eventBus } from '@/services/EventBus';
import { BALANCE_CONFIG } from '@/data/balance/balanceConfig';
import { useInventory } from '@/state';
import { buddyManager, equipmentManager } from '@/systems';
import { BuddyProgressionPanel } from '../progression/BuddyProgressionPanel';
import { BuddyEquipmentModal } from './BuddyEquipmentModal';

interface BuddySlotProps {
  buddy: FishingBuddy;
  onToggle: () => void;
}

export function BuddySlot({ buddy, onToggle }: BuddySlotProps) {
  const [timeUntilNext, setTimeUntilNext] = useState<number>(0);
  const [showCatchAnimation, setShowCatchAnimation] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(buddy.name || '');
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const inventory = useInventory();

  // Check if inventory is full
  const isInventoryFull = inventory.currentCount >= inventory.capacity;

  // Update timer
  useEffect(() => {
    const interval = setInterval(() => {
      if (buddy.nextCatchAt) {
        const remaining = Math.max(0, (buddy.nextCatchAt - Date.now()) / 1000);
        setTimeUntilNext(remaining);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [buddy.nextCatchAt]);

  // Listen for catches by this buddy
  useEffect(() => {
    const unsubscribe = eventBus.on(GameEvent.BUDDY_CAUGHT_FISH, ({ buddySlot }) => {
      if (buddySlot === buddy.slotNumber) {
        // Show catch animation
        setShowCatchAnimation(true);
        setTimeout(() => setShowCatchAnimation(false), 2000);
      }
    });

    return unsubscribe;
  }, [buddy.slotNumber]);

  const progress = buddy.nextCatchAt
    ? Math.min(100, ((BALANCE_CONFIG.FISHING_BUDDY_INTERVAL * 1000 - timeUntilNext * 1000) / (BALANCE_CONFIG.FISHING_BUDDY_INTERVAL * 1000)) * 100)
    : 0;

  const handleSaveName = () => {
    buddyManager.renameBuddy(buddy.slotNumber, editName);
    setIsEditingName(false);
  };

  const handleCancelEdit = () => {
    setEditName(buddy.name || '');
    setIsEditingName(false);
  };

  const displayName = buddy.name || `Buddy ${buddy.slotNumber}`;

  // Calculate buddy's effective stats from progression + equipment
  const buddyMultipliers = calculateStatMultipliers(buddy.progression.stats);
  const equipStats = equipmentManager.calculateBuddyEquipmentStats(buddy.slotNumber);

  // Calculate effective fishing time and catch rate (buddies have reduced effectiveness)
  const baseFishingTime = BALANCE_CONFIG.FISHING_BUDDY_INTERVAL;
  const equipSpeedFactor = 1 - (equipStats.fishingSpeed || 0);
  const effectiveFishingTime = ((baseFishingTime * equipSpeedFactor) / buddyMultipliers.catchSpeed).toFixed(1);
  const effectiveCatchRate = Math.min(100, BALANCE_CONFIG.BASE_CATCH_CHANCE * buddyMultipliers.catchChance * 100);
  const totalQualityBonus = (buddyMultipliers.qualityBonus || 0) + (equipStats.qualityBonus || 0);
  const totalValueBonus = (buddyMultipliers.valueBonus || 0) + (equipStats.valueBonus || 0);

  return (
    <div className="relative">
      {/* Main buddy card */}
      <div
        className={`relative p-6 rounded-xl border-2 transition-all ${
          buddy.enabled
            ? 'bg-blue-900/30 border-blue-500/50 shadow-lg shadow-blue-500/20'
            : 'bg-gray-800/50 border-gray-600/50'
        }`}
      >
        {/* Buddy header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="text-4xl">🧑‍🤝‍🧑</div>
            <div className="flex-1">
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    className="px-2 py-1 bg-gray-700 border border-blue-500 rounded text-sm focus:outline-none focus:border-blue-400"
                    placeholder={`Buddy ${buddy.slotNumber}`}
                    autoFocus
                    maxLength={20}
                  />
                  <button
                    onClick={handleSaveName}
                    className="px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs"
                  >
                    ✓
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-2 py-1 bg-gray-600 hover:bg-gray-700 rounded text-xs"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-lg">{displayName}</h3>
                    <button
                      onClick={() => setIsEditingName(true)}
                      className="text-gray-400 hover:text-white text-sm"
                      title="Rename buddy"
                    >
                      ✏️
                    </button>
                  </div>
                  <p className="text-sm text-gray-400">
                    Auto-fishing {buddy.enabled ? 'active' : 'paused'}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Action buttons */}
          {!isEditingName && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowEquipmentModal(true)}
                className="px-3 py-2 rounded-lg font-medium transition-colors bg-purple-600 hover:bg-purple-700 text-white text-sm"
                title="Manage equipment"
              >
                ⚙️ Equip
              </button>
              <button
                onClick={onToggle}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  buddy.enabled
                    ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {buddy.enabled ? 'Pause' : 'Resume'}
              </button>
            </div>
          )}
        </div>

        {/* Progress bar */}
        {buddy.enabled && (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-400 mb-2">
              <span>
                {isInventoryFull ? 'Waiting for inventory space...' : 'Fishing...'}
              </span>
              {!isInventoryFull && <span>{timeUntilNext.toFixed(1)}s</span>}
            </div>
            <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ease-linear ${
                  isInventoryFull
                    ? 'bg-gradient-to-r from-yellow-600 to-orange-500'
                    : 'bg-gradient-to-r from-blue-500 to-cyan-400'
                }`}
                style={{ width: isInventoryFull ? '100%' : `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 text-sm mb-3">
          <div className="bg-gray-800/50 p-3 rounded-lg">
            <p className="text-gray-400">Fish Caught</p>
            <p className="text-xl font-bold text-cyan-400">
              {buddy.totalFishCaught}
            </p>
          </div>
          <div className="bg-gray-800/50 p-3 rounded-lg">
            <p className="text-gray-400">Value Generated</p>
            <p className="text-xl font-bold text-yellow-400">
              {buddy.totalValueGenerated.toFixed(0)}g
            </p>
          </div>
        </div>

        {/* Progression Panel */}
        <BuddyProgressionPanel buddy={buddy} />

        {/* Buddy Stats Display */}
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
          <div className="bg-gray-700/50 p-2 rounded">
            <div className="text-gray-400">Catch Chance</div>
            <div className="text-white font-bold">
              {Math.round(effectiveCatchRate)}%
            </div>
          </div>
          <div className="bg-gray-700/50 p-2 rounded">
            <div className="text-gray-400">Fishing Time</div>
            <div className="text-white font-bold">
              {effectiveFishingTime}s
            </div>
          </div>
          <div className="bg-gray-700/50 p-2 rounded">
            <div className="text-gray-400">Quality Bonus</div>
            <div className="text-white font-bold">
              +{Math.round(totalQualityBonus * 100)}%
            </div>
          </div>
          <div className="bg-gray-700/50 p-2 rounded">
            <div className="text-gray-400">Value Bonus</div>
            <div className="text-white font-bold">
              +{Math.round(totalValueBonus * 100)}%
            </div>
          </div>
        </div>

        {/* Catch animation overlay - top portion only */}
        {showCatchAnimation && (
          <div className="absolute top-0 left-0 right-0 h-32 flex items-center justify-center bg-green-500/20 rounded-t-xl backdrop-blur-sm animate-pulse">
            <div className="text-center">
              <div className="text-4xl mb-1">🎣</div>
              <p className="text-lg font-bold text-green-400">
                Caught a fish!
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Equipment Modal */}
      {showEquipmentModal && (
        <BuddyEquipmentModal
          buddySlotNumber={buddy.slotNumber}
          onClose={() => setShowEquipmentModal(false)}
        />
      )}
    </div>
  );
}
